import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaCheck, FaFileAlt, FaTruck } from "react-icons/fa";
import CommonService from "../../services/common.service";
import { API_URLS, resolvePublicFileUrl } from "../../constants/api-urls";
import {
  areBuyerCmLogisticsStagesComplete,
  CM_BUYER_LOGISTICS_STAGES,
  isCmPipelineFullyComplete,
  normalizeCmProgressMap,
  normalizeCmStageId,
  parseCmRfqIdParam,
  resolveCmProductionStages,
  unwrapApiData,
} from "./cm/cmProductionPaths";
import "../../assets/styles/cm_delivery_confirmation.scss";
import LinkedLogisticsPanel from "../../components/moduleLogistics/LinkedLogisticsPanel";

function getObj(v) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

const DEFAULT_CHECKLIST = [
  { id: "boxes", label: "All boxes received intact" },
  { id: "codes", label: "Correct product codes verified" },
  { id: "docs", label: "Packing list matches delivery note" },
  { id: "damage", label: "No visible shipping damage" },
  { id: "sample", label: "Sample quality check completed" },
];

function checklistFromSaved(saved) {
  let s = saved;
  if (typeof s === "string" && s.trim()) {
    try {
      s = JSON.parse(s);
    } catch {
      s = null;
    }
  }
  return DEFAULT_CHECKLIST.map((x) => ({
    ...x,
    checked:
      s && typeof s === "object" && Object.prototype.hasOwnProperty.call(s, x.id)
        ? Boolean(s[x.id])
        : x.id !== "sample",
  }));
}

export default function BuyerCmDeliveryConfirmationPage() {
  const { rfqId: rfqIdParam } = useParams();
  const navigate = useNavigate();
  const rfqId = parseCmRfqIdParam(rfqIdParam) || rfqIdParam;
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [checklist, setChecklist] = useState(() =>
    DEFAULT_CHECKLIST.map((x) => ({ ...x, checked: x.id !== "sample" }))
  );
  const [notes, setNotes] = useState("");
  const [deliveryAccepted, setDeliveryAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.BUYER_DELIVERY_CONFIRMATION_GET(rfqId));
      const d = unwrapApiData(res);
      setDetail(d);
      const saved = d?.rfq?.buyerDeliveryChecklist;
      setChecklist(checklistFromSaved(saved));
      setNotes(String(d?.rfq?.buyerDeliveryNotes ?? ""));
      setDeliveryAccepted(!!d?.rfq?.buyerDeliveryAcceptedAt);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [rfqId]);

  useEffect(() => {
    load();
  }, [load]);

  const goToPayment = useCallback(() => {
    if (!detail) return;
    const fulfillment = detail?.fulfillment || {};
    const invoiceHint = fulfillment?.invoice?.invoiceNumber;
    const poHint = detail?.purchaseOrder?.poNumber;
    const derivedCur = detail?.derived || {};
    const orderLabel =
      String(fulfillment?.orderLabel || "").trim() || (poHint ? `PO ${poHint}` : `RFQ #${rfqId}`);
    const summaryLines = [
      invoiceHint ? `Invoice: ${invoiceHint}` : null,
      poHint ? `PO: ${poHint}` : null,
    ].filter(Boolean);
    const searchHint = [invoiceHint, poHint].find((x) => x != null && String(x).trim() !== "");
    const afterPay =
      searchHint != null
        ? `/approve-invoice-list?q=${encodeURIComponent(String(searchHint).trim())}`
        : "/approve-invoice-list";
    navigate("/payment/gateway", {
      state: {
        paymentData: {
          context: "cm_delivery",
          rfqId,
          summaryTitle: `Contract manufacturing · ${orderLabel}`,
          summaryAmount: derivedCur.orderValueFormatted || undefined,
          summaryLines: summaryLines.length ? summaryLines : undefined,
        },
        paymentId: null,
        afterSuccessNavigate: afterPay,
      },
    });
  }, [detail, rfqId, navigate]);

  const pp = detail?.production;
  const snap = detail?.fulfillment || {};
  const progress = normalizeCmProgressMap(pp?.progress ?? pp?.progress_json ?? pp?.progressJson);
  const stages = resolveCmProductionStages(pp?.stages ?? pp?.stages_json ?? pp?.stagesJson, progress);
  const hasBuyerLogisticsInPipeline = stages.some((sid) => CM_BUYER_LOGISTICS_STAGES.includes(normalizeCmStageId(sid)));
  const hasGenerateGrnInPipeline = stages.some((s) => normalizeCmStageId(s) === "generate_grn");
  const grnStatus = String(snap?.grn?.status ?? "").trim().toLowerCase();
  const grnRecorded =
    hasGenerateGrnInPipeline &&
    !!String(snap?.grn?.grnNumber ?? "").trim() &&
    grnStatus !== "draft";
  const buyerLogisticsComplete =
    pp?.exists &&
    areBuyerCmLogisticsStagesComplete(stages, progress, { treatGenerateGrnCompleteIfGrn: grnRecorded });
  const allStagesCompleted = pp?.exists && isCmPipelineFullyComplete(stages, progress);
  const canUseDeliveryPage =
    allStagesCompleted || (hasBuyerLogisticsInPipeline && buyerLogisticsComplete);

  const rfqPayload = useMemo(() => getObj(detail?.rfq?.payloadJson), [detail]);
  const quotePayload = useMemo(() => getObj(detail?.rfq?.quotePayloadJson), [detail]);
  const project = useMemo(() => getObj(rfqPayload?.project), [rfqPayload]);
  const basePrice = useMemo(() => getObj(quotePayload?.basePrice), [quotePayload]);
  const po = detail?.purchaseOrder || null;
  const grnFull = detail?.grn || null;
  const grnDocs = Array.isArray(detail?.grnDocuments) ? detail.grnDocuments : [];
  const derived = detail?.derived || {};

  const productLabel =
    String(
      project?.productCode ||
        project?.product_code ||
        project?.productName ||
        project?.product_name ||
        project?.title ||
        ""
    ).trim() || "—";

  const quantityLabel = (() => {
    const q =
      project?.quantity ??
      project?.orderQuantity ??
      rfqPayload?.quantity ??
      quotePayload?.quantity ??
      basePrice?.quantity;
    if (q == null || q === "") return "—";
    const n = Number(q);
    return Number.isFinite(n) ? `${n.toLocaleString()} units` : String(q);
  })();

  const orderValue = derived.orderValueFormatted || null;

  const manufacturerName = String(detail?.rfq?.manufacturerCompanyName || "Contract manufacturer").trim();
  const batchLabel = snap.orderLabel || (po?.poNumber ? `PO ${po.poNumber}` : `RFQ #${rfqId}`);
  const deliveredLine = snap.grn?.grnNumber
    ? `GRN ${snap.grn.grnNumber} recorded · Status: ${snap.grn.status || "—"}`
    : "Complete GRN in the production workspace when shipment arrives";

  const allChecked = checklist.every((c) => c.checked);

  function toggleCheck(id) {
    setChecklist((prev) => prev.map((c) => (c.id === id ? { ...c, checked: !c.checked } : c)));
  }

  async function handleAcceptDelivery() {
    if (!allChecked) {
      toast.warning("Complete the receiving inspection checklist first.");
      return;
    }
    try {
      setSubmitting(true);
      const checklistPayload = {};
      checklist.forEach((c) => {
        checklistPayload[c.id] = !!c.checked;
      });
      const res = await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.BUYER_DELIVERY_CONFIRMATION_ACCEPT(rfqId), {
        notes: notes.trim() || null,
        checklist: checklistPayload,
      });
      const d = unwrapApiData(res);
      setDetail(d);
      setChecklist(checklistFromSaved(d?.rfq?.buyerDeliveryChecklist));
      setNotes(String(d?.rfq?.buyerDeliveryNotes ?? ""));
      setDeliveryAccepted(!!d?.rfq?.buyerDeliveryAcceptedAt);
      toast.success("Delivery accepted. Thank you for confirming receipt.");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not save confirmation");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="cm-delivery-page container py-4">
        <p className="text-muted">Loading delivery confirmation…</p>
      </div>
    );
  }

  if (!pp?.exists) {
    return <Navigate to="/dashboard/buyer/contract-manufacturers/quotes" replace />;
  }

  if (!canUseDeliveryPage) {
    return <Navigate to={`/dashboard/buyer/contract-manufacturers/production/${rfqId}`} replace />;
  }

  const receivedByDisplay = derived.receivedByLine || "—";
  const conditionSummary = derived.conditionSummary || "—";
  const conditionIsIssue = /issues:/i.test(String(conditionSummary));

  return (
    <div className="cm-delivery-page container py-4">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/dashboard/buyer">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/dashboard/buyer/contract-manufacturers/quotes">Contract manufacturing</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/dashboard/buyer/contract-manufacturers/production/${rfqId}`}>Production</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Delivery confirmation
          </li>
        </ol>
      </nav>

      <LinkedLogisticsPanel
        sourceModule="contract_manufacturing"
        sourceRecordType="manufacturing_final_dispatch"
        sourceRecordId={`${rfqId}:final`}
        linkPurpose="final_dispatch"
        title="Final dispatch logistics"
      />

      <header className="cm-delivery-page__header">
        <div>
          <h1 className="cm-delivery-page__title">Delivery Confirmation — {batchLabel}</h1>
          <p className="cm-delivery-page__subtitle">
            Order: <strong>{snap.orderLabel || "—"}</strong>
            {snap.poNumber ? (
              <>
                {" "}
                · PO <strong>{snap.poNumber}</strong>
              </>
            ) : null}
            <br />
            <span className="text-muted">{deliveredLine}</span>
          </p>
        </div>
        <div className="cm-delivery-page__actions">
          <Link to="/contact" className="btn cm-delivery-page__btn-report">
            Report Issue
          </Link>
          {deliveryAccepted ? (
            <>
              <Link
                to={`/dashboard/buyer/contract-manufacturers/production/${rfqId}/product-tracking`}
                className="btn cm-delivery-page__btn-tracking"
              >
                <FaTruck aria-hidden />
                Product tracking
              </Link>
              <button type="button" className="btn cm-delivery-page__btn-pay-next" onClick={goToPayment}>
                Proceed to payment
              </button>
              <span className="cm-delivery-page__accepted-pill" role="status">
                <FaCheck aria-hidden />
                Delivery accepted
              </span>
            </>
          ) : (
            <button
              type="button"
              className="btn cm-delivery-page__btn-accept"
              onClick={handleAcceptDelivery}
              disabled={!allChecked || submitting}
            >
              <FaCheck aria-hidden />
              {submitting ? "Saving…" : "Accept Delivery"}
            </button>
          )}
        </div>
      </header>

      <div className="row g-4">
        <div className="col-lg-8">
          <section className="cm-delivery-page__section">
            <h2 className="cm-delivery-page__section-title">Delivery details</h2>
            <div className="cm-delivery-page__detail-grid">
              <div>
                <span className="cm-delivery-page__detail-label">Delivery reference</span>
                <p className="cm-delivery-page__detail-value">{snap.shippingManifest?.manifestNumber || "—"}</p>
              </div>
              <div>
                <span className="cm-delivery-page__detail-label">Received by (GRN)</span>
                <p className="cm-delivery-page__detail-value">{receivedByDisplay}</p>
              </div>
              <div>
                <span className="cm-delivery-page__detail-label">Goods receipt (GRN)</span>
                <p className="cm-delivery-page__detail-value">{snap.grn?.grnNumber || grnFull?.grnNumber || "Pending"}</p>
              </div>
              <div>
                <span className="cm-delivery-page__detail-label">Condition (from GRN)</span>
                <p className="cm-delivery-page__detail-value mb-0">
                  <span className={conditionIsIssue ? "text-warning fw-semibold" : "cm-delivery-page__badge-good"}>
                    {conditionSummary}
                  </span>
                </p>
              </div>
              {po?.expectedDeliveryDate ? (
                <div>
                  <span className="cm-delivery-page__detail-label">Expected delivery (PO)</span>
                  <p className="cm-delivery-page__detail-value">{String(po.expectedDeliveryDate)}</p>
                </div>
              ) : null}
              {po?.deliveryTerms ? (
                <div>
                  <span className="cm-delivery-page__detail-label">Delivery terms (PO)</span>
                  <p className="cm-delivery-page__detail-value">{po.deliveryTerms}</p>
                </div>
              ) : null}
            </div>
          </section>

          <section className="cm-delivery-page__section">
            <h2 className="cm-delivery-page__section-title">Proof of delivery</h2>
            {grnDocs.length > 0 ? (
              <ul className="list-unstyled mb-0">
                {grnDocs.map((doc) => {
                  const href = resolvePublicFileUrl(doc.documentPath);
                  return (
                    <li key={doc.id} className="mb-2">
                      <FaFileAlt className="me-2" aria-hidden />
                      <a href={href} target="_blank" rel="noopener noreferrer">
                        {doc.documentName || doc.documentType || "Document"}
                      </a>
                      {doc.documentType ? (
                        <span className="text-muted small ms-2">({doc.documentType})</span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="cm-delivery-page__pod-row">
                <div className="cm-delivery-page__pod-placeholder text-muted small">No GRN attachments on file</div>
              </div>
            )}
            {grnFull?.additionalNotes ? (
              <p className="small text-muted mt-2 mb-0">
                <span className="fw-semibold">GRN notes: </span>
                {grnFull.additionalNotes}
              </p>
            ) : (
              <p className="small text-muted mt-2 mb-0">Documents listed here come from the GRN record in the database.</p>
            )}
          </section>

          <section className="cm-delivery-page__section">
            <h2 className="cm-delivery-page__section-title">Receiving inspection</h2>
            {detail?.rfq?.buyerDeliveryAcceptedAt ? (
              <p className="small text-muted mt-0 mb-2">
                Loaded from database (saved on {new Date(detail.rfq.buyerDeliveryAcceptedAt).toLocaleString()}).
              </p>
            ) : null}
            <ul className="cm-delivery-page__checklist">
              {checklist.map((item) => (
                <li key={item.id} className="cm-delivery-page__check-item">
                  <label htmlFor={`cm-check-${item.id}`}>
                    <input
                      id={`cm-check-${item.id}`}
                      type="checkbox"
                      checked={item.checked}
                      onChange={() => toggleCheck(item.id)}
                      disabled={deliveryAccepted}
                    />
                    {item.label}
                  </label>
                  {item.checked ? <FaCheck className="cm-delivery-page__check-done" aria-label="Done" /> : null}
                </li>
              ))}
            </ul>
          </section>

          <section className="cm-delivery-page__section">
            <h2 className="cm-delivery-page__section-title">Additional notes</h2>
            <textarea
              className="cm-delivery-page__notes"
              placeholder="Optional notes for your records…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={deliveryAccepted}
            />
          </section>

          <section className="cm-delivery-page__section">
            <h2 className="cm-delivery-page__section-title">Payment summary</h2>
            <div className="cm-delivery-page__pay-row">
              <span>Total order value (accepted quote)</span>
              <strong>{orderValue || "—"}</strong>
            </div>
            <div className="cm-delivery-page__pay-cards">
              <div className="cm-delivery-page__pay-card cm-delivery-page__pay-card--paid">
                <span>Payment terms (PO)</span>
                <strong>{po?.paymentTerms?.trim() ? po.paymentTerms : "—"}</strong>
              </div>
              <div className="cm-delivery-page__pay-card cm-delivery-page__pay-card--due">
                <span>Currency</span>
                <strong>{po?.currency?.trim() ? po.currency : "—"}</strong>
              </div>
            </div>
            {snap.invoice?.invoiceNumber ? (
              <p className="small text-muted mb-0 mt-2">
                Invoice: <strong>{snap.invoice.invoiceNumber}</strong> ({snap.invoice.status || "—"})
              </p>
            ) : null}
          </section>
        </div>

        <div className="col-lg-4">
          <div className="cm-delivery-page__sidebar-card">
            <h2 className="cm-delivery-page__section-title">Order status</h2>
            {deliveryAccepted ? (
              <>
                <span className="cm-delivery-page__status-badge" style={{ background: "#dcfce7", color: "#166534" }}>
                  Accepted
                </span>
                <p className="cm-delivery-page__status-note">This delivery has been confirmed on your side.</p>
                <Link
                  to={`/dashboard/buyer/contract-manufacturers/production/${rfqId}/product-tracking`}
                  className="btn cm-delivery-page__btn-tracking w-100 mt-2"
                >
                  <FaTruck className="me-2" aria-hidden />
                  Product tracking
                </Link>
                <button type="button" className="btn cm-delivery-page__btn-pay-next w-100 mt-2" onClick={goToPayment}>
                  Proceed to payment
                </button>
              </>
            ) : (
              <>
                <span className="cm-delivery-page__status-badge">Awaiting acceptance</span>
                <p className="cm-delivery-page__status-note">
                  Please review the delivery and confirm acceptance or report any issues.
                </p>
              </>
            )}
          </div>

          <div className="cm-delivery-page__sidebar-card">
            <h2 className="cm-delivery-page__section-title">Order summary</h2>
            <dl className="row small mb-0">
              <dt className="col-5 text-muted">Order</dt>
              <dd className="col-7">{snap.orderLabel || "—"}</dd>
              <dt className="col-5 text-muted">Product</dt>
              <dd className="col-7">{productLabel}</dd>
              <dt className="col-5 text-muted">Batch / ref</dt>
              <dd className="col-7">{batchLabel}</dd>
              <dt className="col-5 text-muted">Quantity</dt>
              <dd className="col-7">{quantityLabel}</dd>
            </dl>
          </div>

          <div className="cm-delivery-page__sidebar-card">
            <h2 className="cm-delivery-page__section-title">Documents</h2>
            <ul className="cm-delivery-page__doc-list">
              <li className="cm-delivery-page__doc-item">
                <FaFileAlt aria-hidden />
                <Link to="/shipping-manifest-list">Shipping manifests</Link>
              </li>
              <li className="cm-delivery-page__doc-item">
                <FaFileAlt aria-hidden />
                <Link to="/grn-manifest-list">GRN list</Link>
              </li>
              <li className="cm-delivery-page__doc-item">
                <FaFileAlt aria-hidden />
                <Link to="/approve-invoice-list">Invoices</Link>
              </li>
            </ul>
          </div>

          <div className="cm-delivery-page__sidebar-card">
            <h2 className="cm-delivery-page__section-title">Contact</h2>
            <p className="cm-delivery-page__contact-name">{manufacturerName}</p>
            <p className="cm-delivery-page__contact-muted">Contract manufacturer for this RFQ (from company profile).</p>
            <Link to="/contact" className="btn cm-delivery-page__btn-support">
              Contact support
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <Link to={`/dashboard/buyer/contract-manufacturers/production/${rfqId}`} className="btn btn-outline-secondary">
          Back to production workspace
        </Link>
      </div>
    </div>
  );
}
